import React from 'react';
import { Alert, Box, Button, Drawer, Stack, Typography } from '@mui/material';
import { ERROR_SOFT, SUCCESS_SOFT } from '../style/verbaScholaStyles';
import { instructionalPinyinText } from './studentInstructionalPinyin';

/** Notices that came with a saved AI result. The lesson review lists those still open, so the history keeps them one
 *  click away rather than in full a second time. */
export function HistoryNotices({ warnings, label }: { warnings: readonly string[]; label: string }) {
    const [open, setOpen] = React.useState(false);
    if (!warnings.length) return null;
    return <Box data-testid="history-notices">
        <Button size="small" aria-expanded={open} onClick={() => setOpen(value => !value)}>{open ? 'Hide the notices' : `${label} (${warnings.length})`}</Button>
        {open && <Stack spacing={1}>{warnings.map((warning, index) => <Alert severity="warning" key={index}>{warning}</Alert>)}</Stack>}
    </Box>;
}

export function ProposalReviewSurface({ inline, onClose, children }: { inline: boolean; onClose: () => void; children: React.ReactNode }) {
    return inline ? <Box>{children}</Box> : <Drawer anchor="right" open onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', md: '80vw' }, maxWidth: 1100, p: 2 } }}>{children}</Drawer>;
}

/** Match words (and individual Chinese characters), with bounded comparison cost. */
function changedTokens(before: string, after: string) {
    const tokenize = (value: string) => value.split(/(\s+|[\u3400-\u9fff]|[.,!?;:])/u).filter(Boolean);
    const a = tokenize(before), b = tokenize(after);
    const keptA = new Set<number>(), keptB = new Set<number>();
    const highlighted = a.length * b.length <= 400000;
    if (highlighted) {
        const table = Array.from({ length: a.length + 1 }, () => new Uint16Array(b.length + 1));
        for (let i = a.length - 1; i >= 0; i--) for (let j = b.length - 1; j >= 0; j--)
            table[i][j] = a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
        let i = 0, j = 0;
        while (i < a.length && j < b.length) {
            if (a[i] === b[j]) { keptA.add(i++); keptB.add(j++); }
            else if (table[i + 1][j] >= table[i][j + 1]) i++;
            else j++;
        }
    }
    return { a, b, keptA, keptB, highlighted };
}

export function ProposalTextDiff({ before: savedBefore, after: savedAfter }: { before: string; after: string }) {
    const before = instructionalPinyinText(savedBefore), after = instructionalPinyinText(savedAfter);
    const diff = React.useMemo(() => changedTokens(before, after), [before, after]);
    if (!diff.highlighted) return <Box sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        <Typography variant="body2" color="text.secondary">Too long to highlight changes. Compare the complete before and after text below.</Typography>
        <Typography>Before: {before}</Typography>
        <Typography>After: {after}</Typography>
    </Box>;
    return <Box sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        <Typography>Before: {diff.a.map((word, index) => <Box component="span" key={index}
            sx={diff.keptA.has(index) ? {} : { bgcolor: ERROR_SOFT, textDecoration: 'line-through' }}>{word}</Box>)}</Typography>
        <Typography>After: {diff.b.map((word, index) => <Box component="span" key={index}
            sx={diff.keptB.has(index) ? {} : { bgcolor: SUCCESS_SOFT, fontWeight: 600 }}>{word}</Box>)}</Typography>
    </Box>;
}

